import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const NAV = [
  { to: "/activities", label: "Activities" },
  { to: "/directory/eat-drink", label: "Eat & Drink" },
  { to: "/directory/nightlife", label: "Nightlife" },
  { to: "/events?category=live_music", label: "Live Music" },
  { to: "/events", label: "Events" },
  { to: "/directory/activity", label: "Tours" },
  { to: "/news", label: "Stories" },
];

export default function Layout({ children }) {
  const [open, setOpen] = React.useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // For /events?category=live_music we want ONLY the Live Music nav active,
  // and for plain /events we want ONLY Events active. Default NavLink matches on
  // pathname only, so both would highlight. Compute the active link explicitly.
  const isNavActive = (to) => {
    const [toPath, toQuery] = to.split("?");
    if (location.pathname !== toPath) return false;
    if (!toQuery) {
      // For non-query nav (e.g. /events), require the current URL to not carry the
      // sibling's distinguishing param.
      const currentCategory = new URLSearchParams(location.search).get("category");
      if (toPath === "/events" && currentCategory === "live_music") return false;
      return true;
    }
    // Match all params in toQuery
    const want = new URLSearchParams(toQuery);
    const have = new URLSearchParams(location.search);
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="glass sticky top-0 z-50" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="/inairlie-logo.png"
              alt="inAirlie"
              className="h-12 w-auto rounded-md"
              data-testid="logo-image"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => {
              const active = isNavActive(n.to);
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to.includes("?") ? false : true}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`text-sm tracking-wide transition-colors ${active ? "text-navy font-medium" : "text-neutral-600 hover:text-navy"}`}
                >
                  {n.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full" data-testid="user-menu">
                    <User className="w-4 h-4 mr-2" /> {user.name.split(" ")[0]}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-btn" className="rounded-full">
                  Login
                </Button>
                <Button onClick={() => navigate("/register")} data-testid="register-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white">
                  Join free
                </Button>
              </>
            )}
          </div>

          <button className="md:hidden" onClick={() => setOpen((s) => !s)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-black/5 bg-white/95 backdrop-blur">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-neutral-700">
                  {n.label}
                </Link>
              ))}
              <div className="h-px bg-black/5 my-2" />
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-neutral-700">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="py-2 text-left text-neutral-700">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-neutral-700">Login</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="py-2 text-navy font-medium">Join free</Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-ink text-cream/70 mt-16" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-10">
          <div>
            <div className="mb-4">
              <img src="/inairlie-logo.png" alt="inAirlie" className="h-12 w-auto" />
            </div>
            <p className="text-sm leading-relaxed text-cream/55">Your local guide to Airlie Beach and the Whitsundays. Discover, eat, drink, play.</p>
          </div>
          <div>
            <h4 className="font-heading text-cream mb-3">Discover</h4>
            <ul className="space-y-2 text-sm">
              {NAV.slice(0, 5).map((n) => <li key={n.to}><Link to={n.to} className="hover:text-cream">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-cream mb-3">Community</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/events" className="hover:text-cream">Events</Link></li>
              <li><Link to="/news" className="hover:text-cream">Stories</Link></li>
              <li><Link to="/noticeboard" className="hover:text-cream" data-testid="footer-noticeboard-link">Noticeboard</Link></li>
              <li><Link to="/jobs" className="hover:text-cream" data-testid="footer-jobs-link">Jobs</Link></li>
              <li><Link to="/dashboard/submit" className="hover:text-cream">List your venue</Link></li>
              <li><Link to="/feedback" className="hover:text-cream" data-testid="footer-feedback-link">Send feedback</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-cream mb-3">Contact</h4>
            <p className="text-sm text-cream/55">hello@inairlie.com<br />Airlie Beach, QLD, Australia</p>
          </div>
        </div>
        <div className="border-t border-cream/10">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-cream/40 flex flex-col md:flex-row justify-between gap-2">
            <span>© 2026 inAirlie. Made with salty air and sunshine.</span>
            <span>Free for users · Sponsored listings available</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
